import { useEffect, useState } from 'react';
import {
  SimpleGrid,
  Paper,
  Text,
  Group,
  ThemeIcon,
  LoadingOverlay,
  Title,
  Modal,
  Table,
  Badge,
  Button,
  ScrollArea,
  Container,
  Stack,
  Box,
  rem,
  ActionIcon,
  Tooltip,
  Center,
  Progress,
} from '@mantine/core';
import {
  IconCar,
  IconAlertTriangle,
  IconCoin,
  IconDashboard,
  IconFileCertificate,
  IconShieldCheck,
  IconArrowRight,
  IconSearch,
  IconTruck,
  IconBus,
  IconMotorbike,
  IconBackhoe,
  IconTruckDelivery,
  IconClockHour4,
  IconDownload,
} from '@tabler/icons-react';
import { useDisclosure } from '@mantine/hooks';
import dayjs from 'dayjs';
import 'dayjs/locale/tr';
import api from '../../api/axiosConfig';
import { notifications } from '@mantine/notifications';
import { downloadVehicleExcel } from './utils/vehicleExcelDownload';

import VehicleDetailModal from '../../components/VehicleMaintenanceTracking/VehicleDetailModal';

dayjs.locale('tr');

export default function Dashboard() {
  const [stats, setStats] = useState({
    total_vehicles: 0,
    maintenance_alerts: 0,
    insurance_alerts: 0,
    casco_alerts: 0,
    monthly_cost: 0,
    vehicle_counts: {
      Otomobil: 0,
      Kamyonet: 0,
      Kamyon: 0,
      Minibüs: 0,
      Otobüs: 0,
      Motosiklet: 0,
      'İş Makinesi': 0,
    },
  });

  const [loading, setLoading] = useState(true);

  const [listOpened, { open: openList, close: closeList }] = useDisclosure(false);
  const [detailOpened, { open: openDetail, close: closeDetail }] = useDisclosure(false);

  const [alertList, setAlertList] = useState([]);
  const [alertType, setAlertType] = useState('');
  const [loadingList, setLoadingList] = useState(false);
  const [exportingAlert, setExportingAlert] = useState(false);
  const [selectedVehicle, setSelectedVehicle] = useState(null);

  const vehicleTypeConfig = {
    Otomobil: { icon: IconCar, color: 'blue', label: 'Otomobil' },
    Kamyonet: { icon: IconTruck, color: 'cyan', label: 'Kamyonet' },
    Kamyon: { icon: IconTruckDelivery, color: 'indigo', label: 'Kamyon' },
    Minibüs: { icon: IconBus, color: 'teal', label: 'Minibüs' },
    Otobüs: { icon: IconBus, color: 'green', label: 'Otobüs' },
    Motosiklet: { icon: IconMotorbike, color: 'orange', label: 'Motosiklet' },
    'İş Makinesi': { icon: IconBackhoe, color: 'yellow', label: 'İş Makinesi' },
  };

  const fetchStats = async () => {
    try {
      const response = await api.get('/vehicles/dashboard/stats');
      const data = response.data;

      let parsedCounts = {
        Otomobil: 0,
        Kamyonet: 0,
        Kamyon: 0,
        Minibüs: 0,
        Otobüs: 0,
        Motosiklet: 0,
        'İş Makinesi': 0,
      };

      if (data.vehicle_types && Array.isArray(data.vehicle_types)) {
        data.vehicle_types.forEach((item) => {
          if (parsedCounts[item.type] !== undefined) {
            parsedCounts[item.type] = item.count;
          }
        });
      } else if (data.vehicle_counts) {
        parsedCounts = { ...parsedCounts, ...data.vehicle_counts };
      }

      setStats({
        ...data,
        vehicle_counts: parsedCounts,
      });
    } catch (error) {
      console.error('Dashboard verileri çekilemedi:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
  }, []);

  const handleCardClick = async (type) => {
    if (!['inspection', 'insurance', 'casco'].includes(type)) return;

    setAlertType(type);
    setLoadingList(true);
    setAlertList([]);
    openList();

    try {
      const response = await api.get(`/vehicles/dashboard/alerts?type=${type}`);
      setAlertList(response.data || []);
    } catch (error) {
      console.error('Detay listesi çekilemedi:', error);
    } finally {
      setLoadingList(false);
    }
  };

  const handleExportAlert = async () => {
    if (!['inspection', 'insurance', 'casco'].includes(alertType)) return;
    setExportingAlert(true);
    try {
      await downloadVehicleExcel(alertType, { days: 30 });
      notifications.show({ title: 'Excel indiriliyor', message: `${getModalInfo().title} - Tasarım V2`, color: 'teal' });
    } catch (error) {
      notifications.show({ title: 'Excel indirilemedi', message: error.message, color: 'red' });
    } finally {
      setExportingAlert(false);
    }
  };

  const handleVehicleDetailClick = (vehicle) => {
    setSelectedVehicle(vehicle);
    openDetail();
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('tr-TR', {
      style: 'currency',
      currency: 'TRY',
    }).format(amount || 0);
  };

  const getDaysLeft = (dateStr) => {
    const diff = dayjs(dateStr).diff(dayjs(), 'day');
    if (diff < 0) return { text: `${Math.abs(diff)} Gün Geçti`, color: 'red', variant: 'filled' };
    if (diff <= 7) return { text: `${diff} Gün Kaldı`, color: 'orange', variant: 'light' };
    return { text: `${diff} Gün Kaldı`, color: 'yellow', variant: 'outline' };
  };

  const getModalInfo = () => {
    switch (alertType) {
      case 'inspection':
        return { title: 'Muayene Uyarıları', icon: IconAlertTriangle, color: 'red' };
      case 'insurance':
        return { title: 'Trafik Sigortası Bitenler', icon: IconFileCertificate, color: 'orange' };
      case 'casco':
        return { title: 'Kasko Poliçesi Bitenler', icon: IconShieldCheck, color: 'grape' };
      default:
        return { title: 'Araç Listesi', icon: IconCar, color: 'blue' };
    }
  };

  const statData = [
    {
      id: 'total',
      title: 'Toplam Araç',
      value: stats.total_vehicles,
      icon: IconCar,
      color: 'blue',
      desc: 'Filoda kayıtlı toplam aktif araç',
      clickable: false,
    },
    {
      id: 'cost',
      title: 'Aylık Gider',
      value: formatCurrency(stats.monthly_cost),
      icon: IconCoin,
      color: 'green',
      desc: 'Bakım, yakıt ve operasyon giderleri',
      clickable: false,
    },
    {
      id: 'inspection',
      title: 'Muayene Uyarısı',
      value: stats.maintenance_alerts,
      icon: IconAlertTriangle,
      color: 'red',
      desc: 'Yakın tarihte muayenesi dolacak araçlar',
      clickable: true,
    },
    {
      id: 'insurance',
      title: 'Sigorta Uyarısı',
      value: stats.insurance_alerts,
      icon: IconFileCertificate,
      color: 'orange',
      desc: 'Trafik sigortası yenileme zamanı gelenler',
      clickable: true,
    },
    {
      id: 'casco',
      title: 'Kasko Uyarısı',
      value: stats.casco_alerts,
      icon: IconShieldCheck,
      color: 'grape',
      desc: 'Kasko poliçesi sona yaklaşan araçlar',
      clickable: true,
    },
  ];

  const modalInfo = getModalInfo();

  return (
    <Container fluid p={{ base: 'sm', md: 'lg' }}>
      <LoadingOverlay visible={loading} overlayProps={{ radius: 'sm', blur: 2 }} />

      <Stack gap="xl">
        {/* HERO */}
        <Paper
          radius="xl"
          p={{ base: 'lg', md: 'xl' }}
          style={{
            position: 'relative',
            overflow: 'hidden',
            background:
              'linear-gradient(135deg, rgba(15,23,42,0.98) 0%, rgba(30,41,59,0.96) 42%, rgba(37,99,235,0.88) 100%)',
            border: '1px solid rgba(255,255,255,0.08)',
            boxShadow: '0 20px 50px rgba(15,23,42,0.18)',
          }}
        >
          <Box
            style={{
              position: 'absolute',
              top: -80,
              right: -80,
              width: 240,
              height: 240,
              borderRadius: '50%',
              background: 'rgba(255,255,255,0.08)',
            }}
          />
          <Box
            style={{
              position: 'absolute',
              bottom: -60,
              left: -60,
              width: 180,
              height: 180,
              borderRadius: '50%',
              background: 'rgba(96,165,250,0.18)',
            }}
          />

          <Group
            justify="space-between"
            align="flex-start"
            wrap="wrap"
            style={{ position: 'relative', zIndex: 1 }}
          >
            <Stack gap={8}>
              <Group gap="sm">
                <ThemeIcon
                  size={50}
                  radius="xl"
                  variant="black"
                  color="rgba(255,255,255,0.14)"
                >
                  <IconDashboard size={26} color="white" />
                </ThemeIcon>

                <div>
                  <Text size="sm" fw={700} c="rgba(255,255,255,0.72)">
                    Araç Filosu Yönetimi
                  </Text>
                  <Title order={2} fw={900} c="white" style={{ letterSpacing: '-0.8px' }}>
                    Genel Bakış
                  </Title>
                </div>
              </Group>

              <Text size="sm" maw={720} style={{ color: 'rgba(255,255,255,0.78)', lineHeight: 1.7 }}>
                Filonuzun mevcut durumunu, kritik uyarıları ve araç dağılımını tek
                ekrandan takip edebilirsiniz.
              </Text>
            </Stack>

            <Paper
              radius="xl"
              p="md"
              style={{
                minWidth: 240,
                background: 'rgba(255,255,255,0.08)',
                border: '1px solid rgba(255,255,255,0.12)',
                backdropFilter: 'blur(12px)',
              }}
            >
              <Group wrap="nowrap" align="center">
                <ThemeIcon
                  size={42}
                  radius="xl"
                  variant="black"
                  color="rgba(255,255,255,0.14)"
                >
                  <IconClockHour4 size={20} color="white" />
                </ThemeIcon>
                <div>
                  <Text size="xs" fw={700} c="rgba(255,255,255,0.70)">
                    Son Güncelleme
                  </Text>
                  <Text fw={800} c="white">
                    {dayjs().format('DD.MM.YYYY HH:mm')}
                  </Text>
                </div>
              </Group>
            </Paper>
          </Group>
        </Paper>

        {/* KPI */}
        <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }} spacing="lg">
          {statData.map((stat, index) => (
            <Paper
              key={stat.id}
              withBorder
              p="lg"
              radius="xl"
              onClick={() => stat.clickable && handleCardClick(stat.id)}
              style={{
                cursor: stat.clickable ? 'pointer' : 'default',
                background: 'linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)',
                border: '1px solid rgba(226,232,240,0.95)',
                boxShadow: '0 10px 30px rgba(15,23,42,0.05)',
                transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                animation: `fadeUp 420ms ease ${index * 70}ms both`,
              }}
              onMouseEnter={(e) => {
                if (!stat.clickable) return;
                e.currentTarget.style.transform = 'translateY(-4px)';
                e.currentTarget.style.boxShadow = '0 18px 40px rgba(15,23,42,0.10)';
              }}
              onMouseLeave={(e) => {
                if (!stat.clickable) return;
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 10px 30px rgba(15,23,42,0.05)';
              }}
            >
              <Group justify="space-between" align="flex-start">
                <Stack gap={6}>
                  <Text size="xs" c="dimmed" fw={700} tt="uppercase" style={{ letterSpacing: '0.5px' }}>
                    {stat.title}
                  </Text>
                  <Title order={3} fw={900} style={{ fontSize: rem(30), lineHeight: 1.1 }}>
                    {stat.value}
                  </Title>
                </Stack>

                <ThemeIcon size={54} radius="xl" variant="light" color={stat.color}>
                  <stat.icon style={{ width: rem(28), height: rem(28) }} stroke={1.6} />
                </ThemeIcon>
              </Group>

              <Group mt="lg" justify="space-between" align="center">
                <Text size="sm" c="dimmed" style={{ maxWidth: '82%', lineHeight: 1.6 }}>
                  {stat.desc}
                </Text>

                {stat.clickable && (
                  <ActionIcon variant="light" color={stat.color} radius="xl" size="lg">
                    <IconArrowRight size={18} />
                  </ActionIcon>
                )}
              </Group>
            </Paper>
          ))}
        </SimpleGrid>

        {/* Filo dağılımı */}
        <Paper
          radius="xl"
          withBorder
          p="lg"
          style={{
            background: 'linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)',
            boxShadow: '0 10px 30px rgba(15,23,42,0.05)',
          }}
        >
          <Stack gap="lg">
            <Group justify="space-between" align="center" wrap="wrap">
              <div>
                <Title order={4} fw={800}>
                  Filo Dağılımı
                </Title>
                <Text size="sm" c="dimmed">
                  Araç türlerine göre mevcut filo yoğunluğu
                </Text>
              </div>

              <Badge size="lg" radius="xl" variant="light" color="blue">
                {stats.total_vehicles || 0} toplam araç
              </Badge>
            </Group>

            <Paper
              radius="xl"
              p="md"
              style={{
                background: 'var(--mantine-color-gray-0)',
                border: '1px solid var(--mantine-color-gray-2)',
              }}
            >
              <Progress.Root size={24} radius="xl">
                {Object.entries(stats.vehicle_counts || {}).map(([type, count]) => {
                  if (count === 0 || stats.total_vehicles === 0) return null;
                  const config = vehicleTypeConfig[type] || { color: 'gray' };
                  const percent = (count / stats.total_vehicles) * 100;

                  return (
                    <Tooltip
                      label={`${type}: ${count} Adet (%${percent.toFixed(0)})`}
                      key={type}
                    >
                      <Progress.Section value={percent} color={config.color}>
                        <Progress.Label>{percent > 8 ? `${Math.round(percent)}%` : ''}</Progress.Label>
                      </Progress.Section>
                    </Tooltip>
                  );
                })}
              </Progress.Root>
            </Paper>

            <SimpleGrid cols={{ base: 2, sm: 3, md: 4, lg: 7 }} spacing="md">
              {Object.keys(vehicleTypeConfig).map((type) => {
                const config = vehicleTypeConfig[type];
                const count = stats.vehicle_counts?.[type] || 0;

                return (
                  <Paper
                    key={type}
                    withBorder
                    radius="xl"
                    p="md"
                    style={{
                      opacity: count > 0 ? 1 : 0.58,
                      background: '#fff',
                      borderColor: 'rgba(226,232,240,0.95)',
                    }}
                  >
                    <Group justify="space-between" align="center" mb={8}>
                      <ThemeIcon variant="light" color={config.color} size={42} radius="xl">
                        <config.icon size={22} />
                      </ThemeIcon>
                      <Text fw={900} size="xl">
                        {count}
                      </Text>
                    </Group>

                    <Text size="sm" fw={700}>
                      {config.label}
                    </Text>
                    <Text size="xs" c="dimmed">
                      Araç kategorisi
                    </Text>
                  </Paper>
                );
              })}
            </SimpleGrid>
          </Stack>
        </Paper>
      </Stack>

      {/* Liste modalı */}
      <Modal
        opened={listOpened}
        onClose={closeList}
        centered
        size="xl"
        padding="lg"
        radius="xl"
        title={
          <Group gap="sm">
            <ThemeIcon color={modalInfo.color} variant="light" radius="xl" size="lg">
              <modalInfo.icon size={20} />
            </ThemeIcon>
            <Text fw={800}>{modalInfo.title}</Text>
          </Group>
        }
      >
        <Box pos="relative" mih={220}>
          <LoadingOverlay visible={loadingList} zIndex={100} overlayProps={{ radius: 'sm', blur: 1 }} />

          <ScrollArea h={380} offsetScrollbars type="auto">
            <Paper
              withBorder
              radius="lg"
              style={{
                overflow: 'hidden',
                background: '#fff',
              }}
            >
              <Table stickyHeader striped highlightOnHover verticalSpacing="md" horizontalSpacing="md">
                <Table.Thead
                  style={{
                    background: 'linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%)',
                  }}
                >
                  <Table.Tr>
                    <Table.Th>Araç Bilgisi</Table.Th>
                    <Table.Th>Bitiş Tarihi</Table.Th>
                    <Table.Th>Kalan Süre</Table.Th>
                    <Table.Th style={{ textAlign: 'right' }}>İşlem</Table.Th>
                  </Table.Tr>
                </Table.Thead>

                <Table.Tbody>
                  {alertList.length > 0 ? (
                    alertList.map((vehicle) => {
                      let targetDate;
                      if (alertType === 'inspection') targetDate = vehicle.inspection_expiry_date;
                      else if (alertType === 'insurance') targetDate = vehicle.traffic_insurance_expiry;
                      else if (alertType === 'casco') targetDate = vehicle.casco_expiry;

                      const status = getDaysLeft(targetDate);

                      return (
                        <Table.Tr key={vehicle.id}>
                          <Table.Td>
                            <Stack gap={2}>
                              <Text fw={700} size="sm">
                                {vehicle.plate}
                              </Text>
                              <Text size="xs" c="dimmed">
                                {vehicle.brand || '-'}
                              </Text>
                            </Stack>
                          </Table.Td>

                          <Table.Td>
                            <Text size="sm">{dayjs(targetDate).format('DD MMMM YYYY')}</Text>
                          </Table.Td>

                          <Table.Td>
                            <Badge color={status.color} variant={status.variant} radius="xl">
                              {status.text}
                            </Badge>
                          </Table.Td>

                          <Table.Td style={{ textAlign: 'right' }}>
                            <Tooltip label="Detay Görüntüle">
                              <ActionIcon
                                variant="light"
                                color="blue"
                                radius="xl"
                                size="lg"
                                onClick={() => handleVehicleDetailClick(vehicle)}
                              >
                                <IconSearch size={17} />
                              </ActionIcon>
                            </Tooltip>
                          </Table.Td>
                        </Table.Tr>
                      );
                    })
                  ) : (
                    !loadingList && (
                      <Table.Tr>
                        <Table.Td colSpan={4}>
                          <Center py="xl" style={{ flexDirection: 'column' }}>
                            <ThemeIcon color="gray" variant="light" size={68} radius="xl" mb="md">
                              <IconShieldCheck size={34} />
                            </ThemeIcon>
                            <Text c="dimmed" fw={600}>
                              Kritik durumda araç bulunamadı.
                            </Text>
                          </Center>
                        </Table.Td>
                      </Table.Tr>
                    )
                  )}
                </Table.Tbody>
              </Table>
            </Paper>
          </ScrollArea>

          <Group justify="space-between" mt="lg" gap="sm" wrap="wrap">
            <Button
              color="teal"
              variant="light"
              radius="xl"
              leftSection={<IconDownload size={17} />}
              loading={exportingAlert}
              disabled={loadingList || !['inspection', 'insurance', 'casco'].includes(alertType)}
              onClick={handleExportAlert}
            >
              {modalInfo.title} - Excel İndir (V2)
            </Button>
            <Button variant="default" radius="xl" onClick={closeList}>
              Kapat
            </Button>
          </Group>
        </Box>
      </Modal>

      {/* Araç detay modalı */}
      <VehicleDetailModal opened={detailOpened} close={closeDetail} vehicle={selectedVehicle} />

      <style>
        {`
          @keyframes fadeUp {
            from {
              opacity: 0;
              transform: translateY(12px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
        `}
      </style>
    </Container>
  );
}